// This script assumes `pdfjsLib` and `mammoth` are available in the global scope,
// as they are loaded via CDN in index.html.

declare const pdfjsLib: any;
declare const mammoth: any;

export const SCANNED_CHARS_PER_PAGE_THRESHOLD = 50;
export const SCANNED_MAX_PAGES = 100;

export interface ParsedDocument {
  text: string;
  pageCount: number;
  isScanned?: boolean;
  ocrPageFailures?: number[];
}

export interface PdfAnalysisResult {
  pageCount: number;
  isScanned: boolean;
  avgCharsPerPage: number;
  /** Populated for text-based PDFs only */
  text?: string;
  /** Per-page extracted text (index 0 = page 1) for hybrid OCR follow-up */
  pageTexts?: string[];
  /** Pages with little/no extractable text that should be OCR'd in hybrid docs */
  sparsePageNumbers?: number[];
}

export type ExtractionProgress =
  | { phase: 'detecting' }
  | { phase: 'extracting' }
  | { phase: 'ocr'; currentPage: number; totalPages: number };

export function formatPageMarker(pageNumber: number): string {
  return `--- Page ${pageNumber} ---`;
}

export function formatOcrFailurePlaceholder(pageNumber: number): string {
  return `[OCR failed on page ${pageNumber} — original text could not be extracted. Please re-upload or manually review this page.]`;
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (!event.target?.result) {
        reject(new Error('Failed to read file.'));
        return;
      }
      resolve(event.target.result as ArrayBuffer);
    };
    reader.onerror = () => reject(new Error('Error reading file.'));
    reader.readAsArrayBuffer(file);
  });
}

async function extractPdfPageTexts(file: File): Promise<{ pageTexts: string[]; pageCount: number }> {
  const pdfData = await readFileAsArrayBuffer(file);
  const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
  const pageCount = pdf.numPages;
  const pageTexts: string[] = [];

  for (let i = 1; i <= pageCount; i++) {
    const page = await pdf.getPage(i);
    const text = await page.getTextContent();
    pageTexts.push(text.items.map((item: { str: string }) => item.str).join(' '));
  }

  return { pageTexts, pageCount };
}

/**
 * Analyzes a PDF to determine whether it is scanned (image-only) or text-extractable.
 */
export async function analyzePdf(file: File): Promise<PdfAnalysisResult> {
  const { pageTexts, pageCount } = await extractPdfPageTexts(file);
  const totalChars = pageTexts.reduce((sum, pageText) => sum + pageText.trim().length, 0);
  const avgCharsPerPage = pageCount > 0 ? totalChars / pageCount : 0;
  const isScanned = avgCharsPerPage < SCANNED_CHARS_PER_PAGE_THRESHOLD;
  const sparsePageNumbers = pageTexts
    .map((pageText, index) =>
      pageText.trim().length < SCANNED_CHARS_PER_PAGE_THRESHOLD ? index + 1 : null
    )
    .filter((n): n is number => n != null);

  if (isScanned) {
    return { pageCount, isScanned, avgCharsPerPage, sparsePageNumbers };
  }

  const text = pageTexts
    .map((pageText, index) => `${formatPageMarker(index + 1)}\n${pageText}`)
    .join('\n\n');

  return {
    pageCount,
    isScanned,
    avgCharsPerPage,
    text,
    pageTexts,
    sparsePageNumbers,
  };
}

/**
 * Extracts text content from a given file (PDF or DOCX).
 */
export const extractTextFromFile = async (
  file: File,
  options?: {
    onProgress?: (progress: ExtractionProgress) => void;
    knownAnalysis?: PdfAnalysisResult;
  }
): Promise<ParsedDocument> => {
  const fileType = file.type;
  const fileName = file.name.toLowerCase();

  if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return extractTextFromPdf(file, options);
  }

  if (
    fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileName.endsWith('.docx')
  ) {
    return extractTextFromDocx(file);
  }

  throw new Error('Unsupported file type. Please upload a PDF or DOCX file.');
};

const extractTextFromPdf = async (
  file: File,
  options?: {
    onProgress?: (progress: ExtractionProgress) => void;
    knownAnalysis?: PdfAnalysisResult;
  }
): Promise<ParsedDocument> => {
  options?.onProgress?.({ phase: 'detecting' });
  const analysis = options?.knownAnalysis ?? (await analyzePdf(file));

  if (analysis.isScanned) {
    const { ocrScannedPdf } = await import('./pdfOcrService');
    const { text, ocrPageFailures } = await ocrScannedPdf(
      file,
      analysis.pageCount,
      options?.onProgress
    );

    return {
      text,
      pageCount: analysis.pageCount,
      isScanned: true,
      ocrPageFailures,
    };
  }

  options?.onProgress?.({ phase: 'extracting' });

  // Hybrid PDFs: document average looks like text, but some pages are image-only.
  // OCR those sparse pages so they are not silently empty in the redacted export.
  const sparsePages = (analysis.sparsePageNumbers || []).filter(
    (page) => page >= 1 && page <= analysis.pageCount
  );

  if (sparsePages.length > 0 && analysis.pageTexts) {
    const { ocrSelectedPdfPages } = await import('./pdfOcrService');
    const { pageTexts, ocrPageFailures } = await ocrSelectedPdfPages(
      file,
      analysis.pageCount,
      sparsePages,
      analysis.pageTexts,
      options?.onProgress
    );
    const text = pageTexts
      .map((pageText, index) => `${formatPageMarker(index + 1)}\n${pageText}`)
      .join('\n\n');

    return {
      text,
      pageCount: analysis.pageCount,
      isScanned: false,
      ocrPageFailures: ocrPageFailures.length > 0 ? ocrPageFailures : undefined,
    };
  }

  return {
    text: analysis.text ?? '',
    pageCount: analysis.pageCount,
    isScanned: false,
  };
};

const extractTextFromDocx = (file: File): Promise<ParsedDocument> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      if (!event.target?.result) {
        return reject(new Error('Failed to read DOCX file.'));
      }
      try {
        const result = await mammoth.extractRawText({ arrayBuffer: event.target.result });
        const estimatedPages = Math.max(1, Math.ceil(result.value.length / 3000));
        resolve({ text: result.value, pageCount: estimatedPages });
      } catch {
        reject(new Error('Could not parse DOCX file.'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file.'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Gets the page count from a PDF file without extracting all text.
 */
export const getPdfPageCount = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      if (!event.target?.result) {
        return reject(new Error('Failed to read PDF file.'));
      }
      try {
        const pdf = await pdfjsLib.getDocument({ data: event.target.result }).promise;
        resolve(pdf.numPages);
      } catch {
        reject(new Error('Could not read PDF file.'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file.'));
    reader.readAsArrayBuffer(file);
  });
};
